(ns app.front.view.group.admin.ai.files
  (:require [ajax.core :as ajax]
            [app.front.entities :as entities]
            [app.front.file.download :as file-download]
            [app.front.ui.sortable-table :as sortable-table]
            [app.front.util :as futil]
            [app.front.view.attachment.upload :as att-upload]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.subs :as gas]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [>evt <sub]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(def supported-types
  [".pdf" ".docx" ".html"])

(def file-input-id "rag-file-upload-input")

(rf/reg-event-db ::data-entered
  (fn [db [_ md5 field value]]
    (assoc-in db [::pending-uploads md5 field] value)))

(rf/reg-event-db ::cancel-upload
  (fn [db [_ maybe-md5]]
    (doseq [upload (if maybe-md5
                     [(get-in db [::pending-uploads maybe-md5])]
                     (-> db ::pending-uploads vals))]
      (when-let [req (:ajax-request upload)]
        (ajax/abort req)))
    (-> (if maybe-md5
          (p/dissoc-in db [::pending-uploads maybe-md5])
          (assoc db ::pending-uploads {}))
        (dissoc ::file-read-in-progress))))

(rf/reg-event-db ::set-file-read-in-progress
  (fn [db [_ in-progress?]]
    (assoc db ::file-read-in-progress in-progress?)))

(rf/reg-event-fx ::file-added
  (fn [{:keys [db]} [_ {:keys [content-type md5-base64 filename] :as file}]]
    {:db           (assoc-in db [::pending-uploads md5-base64] file)
     :comm/command {:command         :group/initialize-rag-file-upload
                    :data            {:content-type content-type
                                      :md5-digest   md5-base64
                                      :filename     filename
                                      :group-id     (::gas/group-id db)}
                    :on-success      [::start-file-upload file]
                    :on-server-error [::upload-failure md5-base64]}}))

(rf/reg-event-fx ::start-file-upload
  (fn [{:keys [db]} [_ {:keys [md5-base64 content-type file]} _ {:keys [file-id upload-url]}]]
    (let [ajax-req (ajax/PUT upload-url {:headers          {"Content-Type" content-type}
                                         :body             file
                                         :handler          #(>evt [::upload-complete md5-base64 file-id])
                                         :progress-handler #(>evt [::update-progress md5-base64 %])
                                         :error-handler    #(>evt [::upload-failure md5-base64 %])})]
      {:db (assoc-in db [::pending-uploads md5-base64 :ajax-request] ajax-req)})))

(rf/reg-event-db ::upload-complete
  (fn [db [_ md5 file-id]]
    (if (get-in db [::pending-uploads md5])
      (-> db
          (assoc-in [::pending-uploads md5 :file-id] file-id)
          (assoc-in [::pending-uploads md5 :upload-progress] 100)
          (dissoc ::file-read-in-progress))
      db)))

(rf/reg-event-fx ::update-progress
  (fn [{:keys [db]} [_ md5 progress-event]]
    (when-let [pending (get-in db [::pending-uploads md5])]
      (when-let [new-value (att-upload/calculate-progress (:upload-progress pending) progress-event)]
        {:db (assoc-in db [::pending-uploads md5 :upload-progress] new-value)}))))

(rf/reg-event-fx ::upload-failure
  (fn [{:keys [db]} [_ md5 _status-or-error _server-error]]
    {:db       (-> db
                   (assoc-in [::pending-uploads md5 :error] true)
                   (dissoc ::file-read-in-progress))
     :dispatch [:notifications/add :toast :error :error/error :group.ai-files/upload-failed]}))

(rf/reg-event-fx ::save-upload
  (fn [{:keys [db]} [_ group-id {:keys [file-id filename content-type md5-base64 authority-only? description]}]]
    {:db           (update db ::pending-creates (fnil conj #{}) md5-base64)
     :comm/command {:command          :group/upload-rag-file
                    :method           :post
                    :data             {:file-id         file-id
                                       :filename        filename
                                       :content-type    content-type
                                       :group-id        group-id
                                       :md5-digest      md5-base64
                                       :authority-only? (boolean authority-only?)
                                       :description     (or description "")}
                    :on-success       [::save-upload-success md5-base64 group-id]
                    :finally-dispatch [::save-upload-done md5-base64]}}))

(rf/reg-event-fx ::save-upload-success
  (fn [{:keys [db]} [_ md5 group-id _]]
    {:db         (update db ::pending-uploads dissoc md5)
     :dispatch-n [[:notifications/add :toast :success :success :group.ai-files/upload-success]
                  [::list-files group-id]]}))

(rf/reg-event-db ::save-upload-done
  (fn [db [_ md5]]
    (update db ::pending-creates disj md5)))

(rf/reg-event-fx ::extract-rules
  (fn [_ [_ group-id file-id]]
    {:comm/command {:command    :group/trigger-rule-extraction
                    :data       {:group-id group-id
                                 :file-id  file-id}
                    :method     :post
                    :notify     {:success :group.ai-files/rule-extraction-started
                                 :error   :group.ai-files/rule-extraction-failed}
                    :on-success [::entities/apply-changes]}}))

(rf/reg-event-fx ::delete-file
  (fn [_ [_ file-id]]
    {:comm/command {:command    :group/delete-rag-file
                    :data       {:file-id file-id}
                    :method     :post
                    :notify     {:success :group.ai-files/delete-success}
                    :on-success [::remove-file-from-list file-id]}}))

(rf/reg-event-db ::remove-file-from-list
  (fn [db [_ file-id]]
    (update db ::files dissoc file-id)))

(rf/reg-event-fx ::list-files
  (fn [_ [_ group-id]]
    {:comm/command {:command    :group/list-rag-files
                    :method     :get
                    :data       {:group-id group-id}
                    :on-success [::set-files]}}))

(rf/reg-event-db ::set-files
  (fn [db [_ _ files]]
    (assoc db ::files files)))

(rf/reg-sub ::pending-uploads :-> ::pending-uploads)
(rf/reg-sub ::files :-> ::files)
(rf/reg-sub ::file-read-in-progress :-> ::file-read-in-progress)

(rf/reg-sub ::pending-creates?
  (fn [db] (-> db ::pending-creates seq boolean)))

(rf/reg-sub ::all-uploads-ready?
  :<- [::pending-uploads]
  (fn [uploads]
    (every? :file-id (vals uploads))))

(defn- valid-file? [f]
  (and (some #(str/ends-with? (str/lower-case (.-name f)) %) supported-types)
       (< (.-size f) 104857600)))

(defn- process-file-read [file event]
  (let [md5-base64 (-> event .-target .-result js/Uint8Array. att-upload/md5)]
    (>evt [::file-added {:filename        (.-name file)
                         :size            (.-size file)
                         :content-type    (.-type file)
                         :md5-base64      md5-base64
                         :file            file
                         :upload-progress 0
                         :file-id         nil}])))

(defn- read-files [files]
  (let [valid   (filter valid-file? files)
        invalid (remove valid-file? files)]
    (>evt [::set-file-read-in-progress true])
    (when (seq invalid)
      (>evt [:notifications/add :toast :error :error/error :group.ai-files/invalid-files]))
    (if (seq valid)
      (doseq [f valid]
        (let [reader (js/FileReader.)]
          (set! (.-onload reader) (partial process-file-read f))
          (.readAsArrayBuffer reader f)))
      (>evt [::set-file-read-in-progress false]))))

(defn- handle-files [e]
  (when-let [files (some-> e .-target .-files array-seq)]
    (read-files files)))

(defn- handle-file-drop [e]
  (att-upload/stop-event e)
  (when-let [files (some-> e .-dataTransfer .-files array-seq)]
    (read-files files)))

(defn- save-uploads [uploads group-id]
  (doseq [[_ upload] uploads]
    (>evt [::save-upload group-id upload])))

(defn- uploads-list [tr uploads]
  (when (seq uploads)
    (futil/for-frag [[md5 {:keys [filename size upload-progress error authority-only? description]}] uploads]
      ^{:key md5}
      [:div.bg-ui-light-gray.p-4.mb-4
       [:div.flex.justify-between.w-full.items-center
        [:div.flex-1
         (str filename " (" (att-upload/format-size size) ")")]
        [:div.w-16
         (if error
           [att-upload/error-bar (tr [:error/error])]
           [att-upload/progress-bar upload-progress])]
        [:span.controls.w-8
         (cc/icon {:icon        i/trash
                   :description (tr [:upload/remove-attachment])
                   :class-name  "cursor-pointer"
                   :on-click    #(>evt [::cancel-upload md5])})]]
       [:div.flex.flex-row.flex-wrap.w-full.gap-4.mt-2.items-center
        [:div
         (cc/checkbox {:id         (str md5 "-authority-only")
                       :label-text (tr [:group.ai-files/authority-only])
                       :checked    (boolean authority-only?)
                       :on-change  #(>evt [::data-entered md5 :authority-only? %])})]
        [:div.flex-1
         (cc/text-input {:id         (str md5 "-description")
                         :label-text (tr [:group.ai-files/description])
                         :value      (or description "")
                         :on-change  #(>evt [::data-entered md5 :description (-> % .-target .-value)])})]]])))

(defn- metadata-input [tr group-id]
  (let [uploads   (<sub [::pending-uploads])
        disabled? (or (not (<sub [::all-uploads-ready?]))
                      (<sub [::pending-creates?]))]
    [:div.upload
     [uploads-list tr uploads]
     [:div.buttons
      (cc/button {:kind             :primary
                  :icon             i/check
                  :icon-description ""
                  :disabled         disabled?
                  :on-click         #(save-uploads (<sub [::pending-uploads]) group-id)}
                 (tr [:upload/done]))
      (cc/button {:kind             :tertiary
                  :icon             i/remove
                  :icon-description ""
                  :on-click         #(>evt [::cancel-upload])}
                 (tr [:cancel]))]]))

(defn- upload-widget [tr]
  (let [read-pending? (<sub [::file-read-in-progress])]
    [:div.upload-area__drop-area
     {:on-drag-enter att-upload/stop-event
      :on-drag-over  att-upload/drag-over
      :on-drop       handle-file-drop}
     (if read-pending?
       [:div (cc/inline-loading {:description (tr [:upload/preparing-new-files])})]
       [:div.leading-tight
        [:input {:type       "file"
                 :id         file-input-id
                 :multiple   "multiple"
                 :style      {:display "none"}
                 :accept     (str/join "," supported-types)
                 :on-change  handle-files
                 :aria-label (tr [:file/upload])}]
        [:div.upload-area__drop-area__upper
         [:span (tr [:attachments/upload-text1])]
         [:br]
         [:span (cc/link {:href "#" :on-click #(att-upload/open-filepicker file-input-id %)}
                         (tr [:attachments/upload-link])) " " (tr [:attachments/upload-text2])]]
        [:div.upload-area__drop-area__lower.max-w-xl
         [:span (tr [:group.ai-files/upload-help])]]])]))

(defn- upload-section [tr group-id]
  (let [pending-creates? (<sub [::pending-creates?])
        pending-uploads? (seq (<sub [::pending-uploads]))]
    [:div.upload-area.m-8
     (cond
       pending-creates?
       (cc/inline-loading {:description (tr [:file/uploading])})

       pending-uploads?
       [metadata-input tr group-id]

       :else
       [upload-widget tr])]))

(defn- processing-active? [file]
  (contains? #{:file.rag-processing-state/in-progress
               :file.rag-processing-state/rule-extraction-in-progress}
             (:file/rag-processing-state file)))

(defn- rag-processing-state-cell [tr file]
  (case (:file/rag-processing-state file)
    :file.rag-processing-state/in-progress
    (cc/inline-loading {:description (tr [:group.ai-files/rag-state-in-progress])})

    :file.rag-processing-state/rule-extraction-in-progress
    (cc/inline-loading {:description (tr [:group.ai-files/rag-state-in-progress])})

    :file.rag-processing-state/success
    (cc/tag {:type "green"} (tr [:group.ai-files/rag-state-success]))

    :file.rag-processing-state/failed
    (cc/tag {:type "red"} (tr [:group.ai-files/rag-state-failed]))

    :file.rag-processing-state/skipped
    (cc/tag {:type "gray"} (tr [:group.ai-files/rag-state-skipped]))

    nil))

(defn- files-table [tr group-id]
  (let [files        (<sub [::files])
        date-time-fn (<sub [::date-util/date-time-fn])]
    [:div.m-8
     (if (seq files)
       [sortable-table/table
        {:cols         [[(tr [:group.ai-files/file-name])
                         :file/filename
                         {:sorting true}]
                        [(tr [:group.ai-files/description])
                         :file/description
                         {:sorting true}]
                        [(tr [:group.ai-files/uploaded-by])
                         #(->> % :entity/creator ((juxt :user/first-name :user/last-name)) (str/join " "))
                         {:sorting true}]
                        [(tr [:group.ai-files/authority-only])
                         #(-> % :file/metadata :authority-only str str/capitalize)
                         {:sorting true}]
                        [(tr [:group.ai-files/upload-time])
                         (comp date-time-fn :entity/created)
                         {:sorting true}]
                        [(tr [:group.ai-files/file-status])
                         (fn [file] [rag-processing-state-cell tr file])
                         {:sorting (fn [a b]
                                     (compare (some-> a :file/rag-processing-state name)
                                              (some-> b :file/rag-processing-state name)))}]]
         :link         {:make-href   (fn [file]
                                       (file-download/download-href
                                         :file/download-group-file
                                         {:group-id (:domain/authority-group file)
                                          :download true
                                          :file-id  (:db/id file)}))
                        :description (tr [:download])}
         :items        (or (vals files) [])
         :make-actions (fn [file]
                         (let [delete-action {:title     (tr [:delete])
                                              :on-click  #(>evt [::delete-file (:db/id file)])
                                              :icon      i/trash
                                              :disabled? (processing-active? file)}]
                           (if (ff/enabled? :feature-flag.id/wizard-ai-precheck)
                             [{:title           (tr [:group.ai-files/extract-rules])
                               :on-click        #(>evt [::extract-rules group-id (:db/id file)])
                               :icon            i/document-search
                               :confirm?        true
                               :confirm-content (tr [:group.ai-files/extract-rules-confirm])
                               :disabled?       (processing-active? file)}
                              delete-action]
                             [delete-action])))
         :tr           tr}]
       [:p (tr [:group.ai-files/no-files])])]))

(defn section [tr group-id]
  [acommon/section {}
   [upload-section tr group-id]
   [files-table tr group-id]])
